import { Role } from "@/features/dashboard/routes/sidebar.config";
import { ONBOARDING_COMPLETED_STEP } from "@/lib/authDashboardGate";
import type { IdentityStatus } from "@/lib/identityVerification";
import type { User } from "@/store/auth.store";

/** POST /auth/signup — no role or terms in body */
export type AuthSignupRequestBody = {
  email: string;
  firstName: string;
  lastName: string;
  rememberMe: boolean;
  password: string;
};

/** POST /auth/login (or /auth/sign-in — uses same wire shape as signup success payload) */
export type AuthLoginRequestBody = {
  email: string;
  password: string;
  rememberMe: boolean;
};

/** POST /auth/firebase-login (or override via NEXT_PUBLIC_AUTH_FIREBASE_LOGIN_PATH) */
export type AuthFirebaseLoginRequestBody = {
  idToken: string;
};

export type AuthSelectRoleRequestBody = {
  userId: string;
  role: Role.CONTRACTOR | Role.PROPERTY_OWNER;
};

export type AuthAcceptTermsRequestBody = {
  userId: string;
  agreedToTerms: true;
};

export type AuthCompleteOnboardingRequestBody = {
  userId: string;
};

/** POST /auth/request-password-reset */
export type AuthRequestPasswordResetBody = {
  email: string;
};

/** POST /auth/verify-otp (public) */
export type AuthVerifyOtpRequestBody = {
  email: string;
  code: string;
};

export type AuthVerifyOtpResponse = {
  statusCode: number;
  message: string;
  success: boolean;
  token: string;
};

/** POST /auth/reset-password (public) */
export type AuthResetPasswordRequestBody = {
  email: string;
  newPassword: string;
};

export type AuthResetPasswordResponse = {
  statusCode: number;
  message: string;
  success: boolean;
};

/** POST /auth/verify-email (public) */
export type AuthVerifyEmailRequestBody = {
  email: string;
  code: string;
};

export type AuthVerifyEmailResponse = {
  success: boolean;
};

export type AuthSimpleMessageResponse = {
  statusCode: number;
  message: string;
};

/** POST /auth/change-password (authenticated) */
export type AuthChangePasswordRequestBody = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type AuthChangePasswordResponse = AuthSimpleMessageResponse;

/** Contractor profile — aligned with Nest DTO (optional fields, numeric geo/radius). */
export type ContractorProfileServiceArea = {
  area: string;
  areaType: string;
};

export type ContractorProfileRequestBody = {
  userId: string;
  businessName?: string;
  businessEmail?: string;
  businessPhone?: string;
  businessLogo?: string;
  serviceRadiusMiles?: number;
  address?: string;
  propertyName?: string;
  unitOrApartmentNo?: string;
  city?: string;
  serviceArea?: ContractorProfileServiceArea | Record<string, unknown>;
  state?: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
};

export type PropertyOwnerProfileRequestBody = {
  userId: string;
  companyName?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyLogo?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  useCompanyNameAsDisplay?: boolean;
};

export type AuthProfileResponseData = {
  id?: string;
  userId?: string;
  [key: string]: unknown;
};

export type AuthProfileResponse = {
  statusCode: number;
  message: string;
  data?: AuthProfileResponseData;
};

/**
 * User resource as returned by auth APIs (signup, GET /auth/user, etc.).
 * `password` may appear on GET /auth/user wire only — never persist it.
 */
export type AuthApiUser = {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string;
  avatar: string | null;
  coverPhoto?: string | null;
  bio: string | null;
  isActive: boolean;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  agreedToTerms: boolean;
  isBetaUser: boolean;
  role: string;
  firebaseUid: string | null;
  isStripeOnboarded: boolean;
  stripeAccountId: string | null;
  stripeCustomerId: string | null;
  /** Admin-granted, and rare — absent means not eligible. */
  immediatePayoutEligible?: boolean;
  identityVerified?: boolean;
  identityStatus?: IdentityStatus;
  deletedAt: string | null;
  userStatus: string;
  mfaEnabled: boolean;
  mfaRecoveryCodes?: string[];
  mfaSecret?: string | null;
  requiresPasswordUpdate: boolean;
  onboardingStep: string;
  isOnboardingComplete: boolean;
  organizationId?: string | null;
  permissions?: Record<
    string,
    {
      canCreate: boolean;
      canRead: boolean;
      canUpdate: boolean;
      canDelete: boolean;
    }
  >;
  appShell?: "owner" | "web" | "agent";
  jobsScope?: "company" | "assigned";
  teamMemberId?: string | null;
  contractorRoleName?: string | null;
};

export type AuthUserDetailsWire = AuthApiUser & {
  password?: string;
};

export type AuthSignupResponseData = {
  /** Backend may send snake_case or camelCase */
  access_token?: string;
  accessToken?: string;
  expiresIn: string;
  sessionId: string;
  user: AuthApiUser;
};

export type AuthSignupResponse = {
  statusCode: number;
  message: string;
  data: AuthSignupResponseData;
};

export type AuthLoginResponseData = AuthSignupResponseData & {
  requiresPasswordUpdate?: boolean;
  requiresEmailVerification?: boolean;
};

export type AuthLoginResponse = {
  statusCode: number;
  message: string;
  data: AuthLoginResponseData;
};

export type AuthUserDetailsResponse = {
  statusCode: number;
  message: string;
  data: AuthUserDetailsWire;
};

export function parseApiRoleToStoreRole(role: string): Role {
  if (role === Role.CONTRACTOR || role === "CONTRACTOR") return Role.CONTRACTOR;
  if (role === Role.PROPERTY_OWNER || role === "PROPERTY_OWNER") {
    return Role.PROPERTY_OWNER;
  }
  if (role === Role.ADMIN || role === "ADMIN") return Role.ADMIN;
  if (role === Role.AGENT || role === "AGENT") return Role.AGENT;
  if (role === Role.ON_BOARDING || role === "ON_BOARDING") {
    return Role.ON_BOARDING;
  }
  return Role.ON_BOARDING;
}

/** Maps API user (no password field required) into persisted {@link User}. */
export function mapAuthApiUserToStoreUser(u: AuthApiUser): User {
  const role = parseApiRoleToStoreRole(u.role);
  const isAgent = role === Role.AGENT;

  return {
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    phone: u.phone ?? "",
    email: u.email,
    role,
    avatar: u.avatar,
    coverPhoto: u.coverPhoto ?? null,
    bio: u.bio,
    isActive: u.isActive,
    isBetaUser: u.isBetaUser,
    agreedToTerms: u.agreedToTerms,
    emailVerified: u.emailVerified,
    stripeCustomerId: isAgent ? null : u.stripeCustomerId,
    stripeAccountId: isAgent ? null : u.stripeAccountId,
    isStripeOnboarded: isAgent ? false : u.isStripeOnboarded,
    immediatePayoutEligible: isAgent
      ? false
      : (u.immediatePayoutEligible ?? false),
    identityVerified: isAgent ? false : (u.identityVerified ?? false),
    identityStatus: isAgent ? "UNVERIFIED" : (u.identityStatus ?? "UNVERIFIED"),
    createdAt: u.createdAt,
    updatedAt: u.updatedAt,
    onboardingStep: isAgent
      ? ONBOARDING_COMPLETED_STEP
      : u.onboardingStep,
    isOnboardingComplete: isAgent ? true : u.isOnboardingComplete,
    organizationId: u.organizationId ?? null,
    permissions: u.permissions,
    appShell: u.appShell,
    jobsScope: u.jobsScope,
    teamMemberId: u.teamMemberId ?? null,
    contractorRoleName: u.contractorRoleName ?? null,
  };
}

/** GET /auth/user — strip password before mapping to store. */
export function mapAuthUserDetailsWireToStoreUser(
  wire: AuthUserDetailsWire,
): User {
  const { password: _p, ...rest } = wire;
  return mapAuthApiUserToStoreUser(rest);
}
